import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Card } from '../../components/ui/Card';

// "Marka format" Excel export'unun sheet yapısı (kullanıcı tanımlı, sıralı).
// Her sheet kendi keyword listesini taşır; export sırasıyla her keyword için
// sayfada yüklü aylık veriyi (monthly[keyword]) çeker. Yeni sheet eklemek için
// bu diziye { name, keywords:[...] } ekle.
// ⚠️ Asıl kaynak backend: src/config/searchVolumeLayouts.js — /snapshot yanıtında
// `layout` gelir ve bu sabitin yerine kullanılır (aylık cron da aynı düzenle
// Excel üretir). Bu sabit sadece yanıt gelmezse diye yedek.
const BRAND_SHEET_LAYOUT = [
  {
    // Markanın kendi adı — rakiplerden ayrı sayfada. keyword_groups'taki
    // "Marka & Genel" kategorisiyle aynı ad.
    name: 'Marka & Genel',
    keywords: ['high5'],
  },
  {
    name: 'brands',
    keywords: [
      'on', 'on ayakkabı', 'on running', 'on cloud',
      'Hoka', 'Asics', 'Salomon', 'Brooks', 'Saucony', 'Skechers',
      'Nike', 'Adidas', 'Puma', 'New Balance', 'Under Armour',
    ],
  },
  {
    // "Rakip Mağazalar" kategorisi — hariç: spx spor, boyner spor,
    // decathlon türkiye, intersport türkiye, flower shoes, laces
    name: 'Rakip Mağazalar',
    keywords: [
      'intersport', 'decathlon', 'superstep', 'boyner', 'sportive',
      'sneaks up', 'vakkorama', 'beymen', 'flower ayakkabı', 'spx',
    ],
  },
  {
    name: 'new brands',
    // "wise" değil "wise running": çıplak "wise" hacminin neredeyse tamamı para
    // transfer uygulaması Wise (TransferWise) araması (2025: 305.600 vs 60).
    // 'peak design': High5'in ürün feed'inde 305 SKU ile GERÇEKTEN var.
    // 'peak performance' ÇIKARILDI (2026-08-24): feed'de HİÇ yok, 2026-07-20'de
    // marka logoları görselinden okunurken yanlış eşleştirilmiş. Hacminin
    // (2025: 17.370) ne kadarı İsveçli outdoor markası ne kadarı genel İngilizce
    // deyim belirsizdi — WISE ile aynı durum, aynı karar verildi: yanlış rakam
    // raporda hiç durmasın. Müşteri (Arzu Şencan) fark etti.
    keywords: [
      'fabletics', 'saysky', 'unna', 'doxa', 'wise running', '4t2', 'haglöfs',
      'oakley', 'oakley gözlük', 'peak design', 'peak design çanta', 'camelbak',
      'garmin', 'bowers & wilkins', 'hydro flask', 'vivobarefoot', 'biolite', 'cool bottles',
    ],
  },
];

export default function SearchVolumeMatrix() {
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [monthly, setMonthly] = useState({}); // keyword (lower) -> [{year,month,monthly_searches}]
  const [fetchStatus, setFetchStatus] = useState({}); // keyword (lower) -> 'ok' | 'failed'
  const [apiMetrics, setApiMetrics] = useState({}); // keyword (lower) -> { competition, cpcLow, cpcHigh }
  const [fetched, setFetched] = useState(0);
  const [running, setRunning] = useState(false);
  const [repairing, setRepairing] = useState(null); // { done, total } | null
  // Kayıtlı görüntü: sayfa açılışta canlı Keyword Planner yerine bunu okur.
  const [serverLayout, setServerLayout] = useState(null);
  const [snapshotAt, setSnapshotAt] = useState(null); // en son kayıt zamanı (Date)
  // Isı haritası: ay hücresi geçen yılın AYNI ayına göre büyümeyle boyanır
  // (Arzu Şencan, 1 Kas 2026: "marka × ay, renk yıllık büyümeye göre").
  const [yoyColors, setYoyColors] = useState(() => {
    try { return localStorage.getItem('svm_yoy_colors') !== 'off'; } catch (_) { return true; }
  });
  const toggleYoyColors = () => setYoyColors(v => {
    try { localStorage.setItem('svm_yoy_colors', v ? 'off' : 'on'); } catch (_) { /* yoksay */ }
    return !v;
  });
  const abortRef = useRef(false);

  // Add / delete UI state
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addKeyword, setAddKeyword] = useState('');
  const [addCategory, setAddCategory] = useState('');
  const [newCategoryMode, setNewCategoryMode] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState('');
  const [deletingIds, setDeletingIds] = useState({});
  const [exportOpen, setExportOpen] = useState(false);

  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const START_YEAR = 2022;

  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  // Bir kelimenin cari yılda veri gelen SON ayı.
  const lastMonthOf = (arr, year) => {
    if (!Array.isArray(arr)) return 0;
    let last = 0;
    for (const r of arr) {
      if (Number(r.year) === year && r.monthly_searches != null) last = Math.max(last, Number(r.month));
    }
    return last;
  };

  // Son YAYINLANMIŞ ay — takvim ayı değil. Keyword Planner cari ayı (ve çoğu zaman
  // bir öncekini de) henüz yayınlamamış olur; takvim ayına kadar kolon açmak 2026'ya
  // sahte "0" ay ekler ve YTD toplamını olduğundan küçük gösterir.
  const ytdMonth = useMemo(() => {
    let last = 0;
    for (const arr of Object.values(monthly)) {
      if (!Array.isArray(arr)) continue;
      for (const r of arr) {
        if (Number(r.year) === currentYear && r.monthly_searches != null) {
          last = Math.max(last, Number(r.month));
        }
      }
    }
    return last || currentMonth;
  }, [monthly, currentYear, currentMonth]);

  const ytdLabel = `${monthNames[0]}–${monthNames[ytdMonth - 1]}`;

  // Expansion state. Prior years are collapsed by default (year-total only).
  // Click year header → expand to 4 quarters. Click a quarter → expand to 3 months.
  const [expandedYears, setExpandedYears] = useState({});
  const [expandedQuarters, setExpandedQuarters] = useState({});
  const toggleYear = (year) => {
    setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }));
  };
  const toggleQuarter = (year, q) => {
    const key = `${year}-Q${q}`;
    setExpandedQuarters(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Build column list:
  //   current year         → months (Jan..currentMonth)
  //   prior year, collapsed→ single 'year' column (= year total)
  //   prior year, expanded → 4 quarter columns; expanded quarter → 3 month columns
  const columns = useMemo(() => {
    const cols = [];
    const years = [];
    for (let y = START_YEAR; y <= currentYear; y++) years.push(y);

    for (const y of years) {
      if (y === currentYear) {
        // ytdMonth, takvim ayı DEĞİL Google'ın yayınladığı son ay. Takvim ayına
        // kadar kolon açmak her kelimeye sahte bir "0" ay ekliyordu (2026-08-24'te
        // tabloda AUG 26 kolonu vardı ve 125 kelimenin hepsinde 0 yazıyordu) —
        // eksik ölçüm ekranda talep çöküşü gibi görünüyor.
        for (let m = 1; m <= ytdMonth; m++) {
          cols.push({
            type: 'month',
            year: y,
            month: m,
            label: `${monthNames[m - 1]} ${String(y).slice(2)}`,
            yearGroup: y,
          });
        }
      } else if (!expandedYears[y]) {
        cols.push({ type: 'year', year: y, yearGroup: y });
      } else {
        for (let q = 1; q <= 4; q++) {
          const key = `${y}-Q${q}`;
          if (expandedQuarters[key]) {
            const firstM = (q - 1) * 3 + 1;
            for (let m = firstM; m <= firstM + 2; m++) {
              cols.push({
                type: 'month',
                year: y,
                month: m,
                label: monthNames[m - 1],
                yearGroup: y,
                inQuarter: q,
              });
            }
          } else {
            cols.push({
              type: 'quarter',
              year: y,
              quarter: q,
              label: `Q${q}`,
              yearGroup: y,
            });
          }
        }
      }
    }
    return cols;
  }, [currentYear, ytdMonth, expandedYears, expandedQuarters]);

  // Group columns by year for the top header row (with colspan).
  const yearGroups = useMemo(() => {
    const out = [];
    let last = null;
    for (const c of columns) {
      if (!last || last.year !== c.yearGroup) {
        last = { year: c.yearGroup, span: 0 };
        out.push(last);
      }
      last.span += 1;
    }
    return out;
  }, [columns]);

  const grouped = useMemo(() => {
    const out = [];
    const seen = new Map();
    for (const row of groups) {
      const key = row.category;
      if (!seen.has(key)) {
        seen.set(key, out.length);
        out.push({ category: key, items: [] });
      }
      out[seen.get(key)].items.push(row);
    }
    return out;
  }, [groups]);

  const totalKeywords = groups.length;

  const sleep = (ms) => new Promise(r => setTimeout(r, ms));

  // Returns { ok: true, monthly } or { ok: false, error }.
  // Distinguishes a real "no data" response (ok with empty array)
  // from a failure (network/HTTP error or thrown exception).
  const fetchMonthlyOnce = async (keyword, expect = null) => {
    const brandId = localStorage.getItem('currentBrandId');
    try {
      const res = await fetch('https://api.glowytics.com/api/search-volume/monthly', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) },
        body: JSON.stringify({
          keyword,
          months: 0,
          ...(expect ? { expectYear: expect.year, expectMonth: expect.month } : {}),
        }),
      });
      if (!res.ok) {
        return { ok: false, error: `HTTP ${res.status}` };
      }
      const data = await res.json();
      return {
        ok: true,
        monthly: Array.isArray(data.monthly) ? data.monthly : [],
        competition: data.competition || null,
        cpcLow: data.top_of_page_bid_low != null ? data.top_of_page_bid_low : null,
        cpcHigh: data.top_of_page_bid_high != null ? data.top_of_page_bid_high : null,
        tailVerified: data.tailVerified ?? null,
      };
    } catch (e) {
      return { ok: false, error: e?.message || 'network error' };
    }
  };

  // Retry with exponential backoff: 500ms → 1500ms → 4500ms.
  const fetchMonthlyForKeyword = async (keyword, expect = null) => {
    const delays = [500, 1500, 4500];
    let lastErr = null;
    for (let attempt = 0; attempt < delays.length + 1; attempt++) {
      if (abortRef.current) return { ok: false, error: 'aborted' };
      const r = await fetchMonthlyOnce(keyword, expect);
      if (r.ok) return r;
      lastErr = r.error;
      if (attempt < delays.length) await sleep(delays[attempt]);
    }
    return { ok: false, error: lastErr || 'unknown' };
  };

  // ---- İki geçişli çekim -------------------------------------------------
  // Keyword Planner aynı isteğe ~%20 olasılıkla serinin SON ayını atarak yanıt
  // veriyor (HTTP 200, hata yok, dizi bir ay kısa — 2026-08-24'te 40 çağrının
  // 8'i). Tek geçişte 121 kelimenin ~24'ü son ayı eksik görünüyor ve tablo bunu
  // "talep düştü" gibi okutuyor. Bu yüzden:
  //   1. geçiş: her kelimeyi bir kez çek.
  //   2. geçiş: TÜM kelimelerin gördüğü en son ay = yayınlanmış ay kabul edilir
  //      (121 kelimenin hepsinin aynı anda kesilme olasılığı sıfıra yakın);
  //      o aydan geride kalan kelimeler, o ayı isteyerek yeniden çekilir.
  // 2. geçişte de gelmeyen kelime "hacim yok" DEĞİL "ölçüm gelmedi" sayılır ve
  //    tabloda/toplamda ayrı işaretlenir — sessizce 0 yazmak müşteriyi yanılttı.
  const runFetch = async (list, baseCount = 0) => {
    setRunning(true);
    abortRef.current = false;
    setFetched(baseCount);

    const collected = {};
    for (const row of list) {
      if (abortRef.current) break;
      const kw = row.keyword;
      const kwKey = kw.toLowerCase();
      const r = await fetchMonthlyForKeyword(kw);
      if (r.ok) {
        collected[kwKey] = r.monthly;
        setMonthly(prev => ({ ...prev, [kwKey]: r.monthly }));
        setFetchStatus(prev => ({ ...prev, [kwKey]: 'ok' }));
        if (r.competition != null || r.cpcLow != null || r.cpcHigh != null) {
          setApiMetrics(prev => ({ ...prev, [kwKey]: { competition: r.competition, cpcLow: r.cpcLow, cpcHigh: r.cpcHigh } }));
        }
      } else {
        setFetchStatus(prev => ({ ...prev, [kwKey]: 'failed' }));
      }
      setFetched(n => n + 1);
    }

    // 2. geçiş — kuyruk onarımı
    if (!abortRef.current) {
      // Yayınlanmış ay, bu turda çekilenlerin YANI SIRA sayfada zaten yüklü olan
      // kelimelerden de okunur: "Retry Failed" / "tekrar dene" gibi alt küme
      // çağrılarında sadece alt kümeye bakmak eşiği olduğundan düşük tespit eder
      // ve onarım hiç tetiklenmez.
      let publishedMonth = 0;
      for (const arr of [...Object.values(collected), ...Object.values(monthly)]) {
        publishedMonth = Math.max(publishedMonth, lastMonthOf(arr, currentYear));
      }
      const short = publishedMonth
        ? list.filter(row => {
            const arr = collected[row.keyword.toLowerCase()];
            return Array.isArray(arr) && arr.length && lastMonthOf(arr, currentYear) < publishedMonth;
          })
        : [];
      if (short.length) {
        setRepairing({ done: 0, total: short.length });
        for (const row of short) {
          if (abortRef.current) break;
          const kwKey = row.keyword.toLowerCase();
          const r = await fetchMonthlyForKeyword(row.keyword, { year: currentYear, month: publishedMonth });
          if (r.ok) {
            collected[kwKey] = r.monthly;
            setMonthly(prev => ({ ...prev, [kwKey]: r.monthly }));
          }
          setRepairing(p => ({ ...p, done: p.done + 1 }));
        }
        setRepairing(null);
      }
    }

    setRunning(false);
    // Her başarılı /monthly çekimi backend'de kayda yazılıyor.
    setSnapshotAt(new Date());
  };

  const runFetchAll = (list) => runFetch(list);

  const runFetchFailed = () => {
    const list = groups.filter(g => fetchStatus[g.keyword.toLowerCase()] === 'failed');
    if (list.length) runFetch(list);
  };

  const failedCount = useMemo(
    () => groups.filter(g => fetchStatus[g.keyword.toLowerCase()] === 'failed').length,
    [groups, fetchStatus]
  );

  // ===== Export helpers =====
  // Always export the FULL data set (all months 2022..current) regardless of UI expand state.
  // Rapor penceresinin son ayı hâlâ gelmemiş kelimeler (2. geçiş sonrası).
  const missingTailKeywords = useMemo(() => {
    if (!ytdMonth) return [];
    return groups
      .filter(g => {
        const arr = monthly[g.keyword.toLowerCase()];
        if (!Array.isArray(arr) || !arr.length) return false;
        return lastMonthOf(arr, currentYear) < ytdMonth;
      })
      .map(g => g.keyword);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, monthly, ytdMonth, currentYear]);

  // Eksik kuyruklu kelimeleri tekrar dene (manuel).
  const runFetchShortTails = () => {
    const set = new Set(missingTailKeywords.map(k => k.toLowerCase()));
    const list = groups.filter(g => set.has(g.keyword.toLowerCase()));
    if (list.length) runFetch(list);
  };

  const fullMonthColumns = useMemo(() => {
    const cols = [];
    for (let y = START_YEAR; y <= currentYear; y++) {
      const lastM = y === currentYear ? ytdMonth : 12;
      for (let m = 1; m <= lastM; m++) {
        cols.push({ year: y, month: m, label: `${monthNames[m - 1]} ${y}` });
      }
    }
    return cols;
  }, [currentYear, ytdMonth]);

  const buildExportRows = () => {
    const headerLabels = [
      'Category', 'Keyword', 'Avg/mo', '12mo Total (export)', '3mo Δ %', 'Competition',
      'CPC Low (₺)', 'CPC High (₺)',
      ...fullMonthColumns.map(c => c.label),
      `${currentYear} YTD (${ytdLabel})`,
      'YTD penceresi',
    ];
    const rows = groups.map(g => {
      const kwKey = g.keyword.toLowerCase();
      const arr = monthly[kwKey];
      // Ölçüm gelmeyen cari yıl ayı 0 DEĞİL 'n/a' — 0, Excel'de düşüş grafiği çizer.
      const kwLastMonth = lastMonthOf(arr, currentYear);
      const rowShort = kwLastMonth > 0 && kwLastMonth < ytdMonth;
      const monthValues = fullMonthColumns.map(c => {
        if (!Array.isArray(arr)) return null;
        if (rowShort && c.year === currentYear && c.month > kwLastMonth) return 'n/a';
        const r = arr.find(x => Number(x.year) === c.year && Number(x.month) === c.month);
        return r ? Number(r.monthly_searches || 0) : 0;
      });
      // Eskiden 2022'den bugüne TÜM ayların toplamıydı — hiçbir dönemi temsil etmeyen
      // bir sayı. Artık cari yılın YTD'si (müşteri geri bildirimi, 2026-07).
      const total = yearTotal(arr, currentYear, ytdMonth);
      return {
        category: g.category,
        keyword: g.keyword,
        avgMonthly: g.avgMonthly ?? computeAvgMonthly(kwKey),
        last12moTotal: g.last12moTotal,
        change3moPct: g.change3moPct ?? compute3moChange(kwKey),
        competition: g.competition || apiMetrics[kwKey]?.competition || null,
        cpcLow: g.cpcLow != null ? Number(g.cpcLow) : (apiMetrics[kwKey]?.cpcLow ?? null),
        cpcHigh: g.cpcHigh != null ? Number(g.cpcHigh) : (apiMetrics[kwKey]?.cpcHigh ?? null),
        monthValues,
        total,
        // Satırın kendi penceresi rapor penceresinden kısaysa toplam kıyaslanamaz.
        totalWindow: rowShort ? `${monthNames[0]}–${monthNames[kwLastMonth - 1]}` : ytdLabel,
      };
    });
    return { headerLabels, rows };
  };

  const todayStamp = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };

  const exportExcel = () => {
    const { headerLabels, rows } = buildExportRows();
    const sheetRows = [headerLabels];
    for (const r of rows) {
      sheetRows.push([
        r.category, r.keyword,
        r.avgMonthly ?? '', r.last12moTotal ?? '',
        r.change3moPct == null ? '' : r.change3moPct,
        r.competition ?? '',
        r.cpcLow ?? '', r.cpcHigh ?? '',
        ...r.monthValues.map(v => v == null ? '' : v),
        r.total ?? '',
        r.totalWindow,
      ]);
    }
    const ws = XLSX.utils.aoa_to_sheet(sheetRows);
    // Column widths
    const widths = [
      { wch: 22 }, { wch: 28 }, { wch: 10 }, { wch: 14 }, { wch: 8 }, { wch: 10 }, { wch: 10 }, { wch: 10 },
      ...fullMonthColumns.map(() => ({ wch: 10 })),
      { wch: 14 }, { wch: 14 },
    ];
    ws['!cols'] = widths;
    ws['!freeze'] = { xSplit: 2, ySplit: 1 };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Search Volume');
    XLSX.writeFile(wb, `search-volume-matrix_${todayStamp()}.xlsx`);
  };

  // "Marka format" — referans Excel düzeni (montly_brand_search_volume):
  //   • Kategori başına ayrı sheet
  //   • Her keyword bir blok: Month satırları (Oca–Ara) × yıl kolonları (2022..currentYear)
  //   • HER yıl kolonu kendi 3-renkli (kırmızı→sarı→yeşil) conditional formatında
  // Conditional formatting yazımı SheetJS community'de yok; backend exceljs ile üretir.
  const [exportingBrand, setExportingBrand] = useState(false);
  const exportExcelBrandFormat = async () => {
    if (exportingBrand) return;
    setExportingBrand(true);
    try {
      const brandId = localStorage.getItem('currentBrandId');
      const payload = {
        startYear: START_YEAR,
        currentYear,
        sheets: (serverLayout || BRAND_SHEET_LAYOUT).map(s => ({
          name: s.name,
          keywords: s.keywords.map(kw => ({
            keyword: kw,
            monthly: Array.isArray(monthly[kw.toLowerCase()]) ? monthly[kw.toLowerCase()] : [],
          })),
        })),
      };
      const res = await fetch('https://api.glowytics.com/api/search-volume/export-xlsx', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let msg = 'Export başarısız';
        try { const j = await res.json(); msg = j.error || msg; } catch (_) { /* non-json */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `search-volume-matrix_marka-format_${todayStamp()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Renkli Excel export edilemedi: ${e?.message || e}`);
    } finally {
      setExportingBrand(false);
    }
  };

  // Helper: yearly totals from monthly array.
  // maxMonth: her yılı aynı pencerede kesmek için. Tam yılı kısmi yılın yanına koymak
  // talebin düşmüş gibi okunuyor — kıyas geçerli olsun diye hepsi aynı aralıkta.
  const yearTotal = (arr, year, maxMonth = 12) => {
    if (!Array.isArray(arr)) return null;
    let sum = 0, any = false;
    for (const r of arr) {
      if (Number(r.year) === year && Number(r.month) <= maxMonth) {
        sum += Number(r.monthly_searches || 0);
        any = true;
      }
    }
    return any ? sum : null;
  };

  const exportPDF = async () => {
    const [glowyticsLogo, high5Logo] = await Promise.all([
      loadImageAsBase64('/logoicon.png'),
      loadImageAsBase64('/high5logo.svg'),
    ]);
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 32;

    // Title block with High5 logo
    let titleX = margin;
    if (high5Logo) {
      doc.addImage(high5Logo, 'PNG', margin, 12, 80, 33);
      titleX = margin + 88;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('High5 Search Volume Analysis', titleX, 34);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(110);
    doc.text(`Date: ${todayStamp()}  ·  Source: Google Ads Keyword Planner  ·  Geo: Turkey  ·  ${groups.length} keywords`, margin, 54);
    doc.setFontSize(8);
    doc.text('Data sourced from Google Ads Keyword Planner API — based on actual search volumes, not estimates. Numbers are rounded by Google.', margin, 66);
    doc.setTextColor(0);

    // Prior years (yearly totals) + current year months
    const priorYears = [];
    for (let y = START_YEAR; y < currentYear; y++) priorYears.push(y);
    const currentMonths = [];
    for (let m = 1; m <= ytdMonth; m++) currentMonths.push(m);

    const fmtNum = (n) => n == null || n === '' ? '—' : Number(n).toLocaleString('tr-TR');

    // Build body grouped by category with section header rows.
    const head = [[
      'Keyword',
      ...priorYears.map(y => `${y} (${ytdLabel})`),
      ...currentMonths.map(m => `${monthNames[m - 1]} ${String(currentYear).slice(2)}`),
      `${currentYear} YTD`,
    ]];

    const body = [];
    const sectionRows = []; // indices to style as section headers
    let runningIdx = 0;
    for (const g of grouped) {
      // Section header row spanning all columns
      body.push([{
        content: `${g.category}  ·  ${g.items.length} keyword`,
        colSpan: head[0].length,
        styles: {
          fillColor: [219, 234, 254],
          textColor: [30, 64, 175],
          fontStyle: 'bold',
          halign: 'left',
          fontSize: 9,
        },
      }]);
      sectionRows.push(runningIdx);
      runningIdx += 1;

      for (const row of g.items) {
        const kwKey = row.keyword.toLowerCase();
        const arr = monthly[kwKey];
        const total = yearTotal(arr, currentYear, ytdMonth);
        // Ölçüm gelmeyen ayı "0" basma: müşteri raporu okurken bunu kategori
        // genelindeki düşüşe örnek olarak kullandı. Gelmeyen ay "n/a", satırın
        // toplamı da kendi penceresiyle etiketlenir.
        const kwLastMonth = lastMonthOf(arr, currentYear);
        const rowShort = kwLastMonth > 0 && kwLastMonth < ytdMonth;
        const monthVal = (m) => {
          if (!Array.isArray(arr)) return null;
          if (rowShort && m > kwLastMonth) return 'n/a';
          const r = arr.find(x => Number(x.year) === currentYear && Number(x.month) === m);
          return r ? Number(r.monthly_searches || 0) : 0;
        };
        body.push([
          rowShort ? `${row.keyword} *` : row.keyword,
          ...priorYears.map(y => fmtNum(yearTotal(arr, y, ytdMonth))),
          ...currentMonths.map(m => { const v = monthVal(m); return v === 'n/a' ? 'n/a' : fmtNum(v); }),
          rowShort ? `${fmtNum(total)} *` : fmtNum(total),
        ]);
        runningIdx += 1;
      }
    }

    autoTable(doc, {
      startY: 78,
      head,
      body,
      margin: { left: margin, right: margin, bottom: 36 },
      styles: { fontSize: 8.5, cellPadding: 4, overflow: 'linebreak', valign: 'middle', lineColor: [226, 232, 240], lineWidth: 0.3 },
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold', fontSize: 8.5, halign: 'center' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 140, fontStyle: 'bold' }, // Keyword
      },
      didParseCell: (data) => {
        const totalCols = head[0].length;
        if (data.section === 'body' && data.column.index >= 1) {
          data.cell.styles.halign = 'right';
        }
        // Highlight current-year month cols (rightmost block before Total)
        const firstCurMonthCol = 1 + priorYears.length;
        const lastCurMonthCol = firstCurMonthCol + currentMonths.length - 1;
        if (data.section === 'body' && data.column.index >= firstCurMonthCol && data.column.index <= lastCurMonthCol) {
          data.cell.styles.fillColor = data.row.index % 2 === 0 ? [239, 246, 255] : [219, 234, 254];
        }
        // Total col bold
        if (data.section === 'body' && data.column.index === totalCols - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [241, 245, 249];
        }
      },
      didDrawPage: () => {
        if (glowyticsLogo) {
          doc.addImage(glowyticsLogo, 'PNG', margin, pageH - 28, 14, 14);
        }
        const str = `${doc.internal.getNumberOfPages()}`;
        doc.setFontSize(8);
        doc.setTextColor(140);
        doc.text('Powered by Glowytics', margin + (glowyticsLogo ? 20 : 0), pageH - 17);
        doc.text(str, pageW - margin, pageH - 17, { align: 'right' });
        doc.setTextColor(0);
      },
    });

    // Dipnot: yıldızlı satırların penceresi diğerlerinden kısa. Bu not olmadan
    // "n/a" ile "0" aynı görünüyor ve eksik ölçüm talep düşüşü sanılıyor.
    if (missingTailKeywords.length) {
      const y = (doc.lastAutoTable?.finalY || 78) + 16;
      doc.setFontSize(8);
      doc.setTextColor(180, 83, 9);
      doc.text(
        `* Google Keyword Planner bu kelimeler icin ${monthNames[ytdMonth - 1]} ${currentYear} verisi dondurmedi: ` +
        `${missingTailKeywords.join(', ')}. Ilgili ay "n/a" olarak isaretlendi; bu satirlarin YTD toplami daha kisa bir ` +
        `pencereyi kapsiyor ve diger kelimelerle birebir kiyaslanamaz. Eksik olcum, talep dususu DEGILDIR.`,
        margin, y, { maxWidth: pageW - margin * 2 }
      );
      doc.setTextColor(0);
    }

    doc.save(`high5-search-volume-analysis_${todayStamp()}.pdf`);
  };

  // StrictMode dev'de effect'i iki kez çalıştırıyor; korumasız hali 125 kelimeyi
  // İKİ KEZ çekiyordu (ilerleme %136'ya çıkıyordu). Keyword Planner dev-token QPS'i
  // düşük — gereksiz ikinci tur RESOURCE_EXHAUSTED'ı ve dolayısıyla eksik veriyi
  // besliyor. Frontend prod'da da CRA dev server olarak koştuğu için bu canlıda geçerli.
  const didLoadRef = useRef(false);

  useEffect(() => {
    if (didLoadRef.current) {
      // StrictMode'un ikinci çağrısı. Yeni tur BAŞLATMA ama birincinin cleanup'ının
      // koyduğu iptali geri al — yoksa tek tur da başlar başlamaz kendini durdurur.
      abortRef.current = false;
      return;
    }
    didLoadRef.current = true;
    const load = async () => {
      try {
        setLoadingGroups(true);
        const brandId = localStorage.getItem('currentBrandId');
        const res = await fetch('https://api.glowytics.com/api/search-volume/keyword-groups', {
          credentials: 'include',
          headers: { ...(brandId ? { 'X-Brand-ID': brandId } : {}) },
        });
        if (!res.ok) {
          setGroups([]);
          return;
        }
        const data = await res.json();
        const list = Array.isArray(data.groups) ? data.groups : [];
        setGroups(list);
        if (!list.length) return;

        // Önce kayıtlı görüntü. Veri ayda bir değişiyor (Google geçen ayı ayın
        // ortasında yayınlıyor, cron 16'sında tazeliyor) — her açılışta 130 kelimeyi
        // Keyword Planner'dan yeniden çekmeye gerek yok. Kayıtta olmayan kelimeler
        // (yeni eklenen vb.) canlı çekilir ve backend onları da kayda yazar.
        let snapRows = [];
        try {
          const sres = await fetch('https://api.glowytics.com/api/search-volume/snapshot', {
            credentials: 'include',
            headers: { ...(brandId ? { 'X-Brand-ID': brandId } : {}) },
          });
          if (sres.ok) {
            const sdata = await sres.json();
            snapRows = Array.isArray(sdata.rows) ? sdata.rows : [];
            if (Array.isArray(sdata.layout) && sdata.layout.length) setServerLayout(sdata.layout);
          }
        } catch (_) { /* kayıt yoksa canlı çekime düş */ }

        const byKey = new Map(snapRows.map(r => [String(r.keywordKey || r.keyword).toLowerCase(), r]));
        const have = list.filter(g => byKey.has(g.keyword.toLowerCase()));
        if (have.length) {
          const m = {}, st = {}, met = {};
          let latest = 0;
          for (const g of have) {
            const r = byKey.get(g.keyword.toLowerCase());
            const k = g.keyword.toLowerCase();
            m[k] = Array.isArray(r.monthly) ? r.monthly : [];
            st[k] = 'ok';
            if (r.competition != null || r.cpcLow != null || r.cpcHigh != null) {
              met[k] = { competition: r.competition, cpcLow: r.cpcLow, cpcHigh: r.cpcHigh };
            }
            const t = r.fetchedAt ? new Date(r.fetchedAt).getTime() : 0;
            if (t > latest) latest = t;
          }
          setMonthly(m);
          setFetchStatus(st);
          setApiMetrics(met);
          setFetched(have.length);
          if (latest) setSnapshotAt(new Date(latest));
        }
        const missing = list.filter(g => !byKey.has(g.keyword.toLowerCase()));
        if (missing.length) runFetch(missing, have.length);
      } finally {
        setLoadingGroups(false);
      }
    };
    load();
    return () => { abortRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getMonthVolume = (keyword, year, month) => {
    const arr = monthly[keyword.toLowerCase()];
    if (!Array.isArray(arr)) return null;
    const row = arr.find(r => Number(r.year) === year && Number(r.month) === month);
    if (!row) return 0;
    return Number(row.monthly_searches || 0);
  };

  const getCellValue = (keyword, col) => {
    const arr = monthly[keyword.toLowerCase()];
    if (!Array.isArray(arr)) return null;
    if (col.type === 'month') return getMonthVolume(keyword, col.year, col.month);
    if (col.type === 'year') {
      let sum = 0;
      let any = false;
      for (const r of arr) {
        if (Number(r.year) === col.year) {
          sum += Number(r.monthly_searches || 0);
          any = true;
        }
      }
      return any ? sum : null;
    }
    // quarter
    const firstM = (col.quarter - 1) * 3 + 1;
    let sum = 0;
    let any = false;
    for (let m = firstM; m <= firstM + 2; m++) {
      const v = getMonthVolume(keyword, col.year, m);
      if (v !== null) { sum += v; any = true; }
    }
    return any ? sum : null;
  };

  // ---- Yıllık büyüme ısı haritası ------------------------------------------
  // Geçen yılın aynı ayı YOKSA (ya da 0'sa) hücre boyanmaz — yeni eklenen
  // kelimede sahte "büyüme" göstermemek için.
  const rawMonth = (keyword, year, month) => {
    const arr = monthly[keyword.toLowerCase()];
    if (!Array.isArray(arr)) return null;
    const r = arr.find(x => Number(x.year) === year && Number(x.month) === month);
    return r && r.monthly_searches != null ? Number(r.monthly_searches) : null;
  };
  const yoyPct = (cur, prev) => (cur == null || !prev ? null : Math.round(((cur - prev) / prev) * 100));
  const yoyStyle = (pct) => {
    if (pct == null || Math.abs(pct) < 3) return undefined;
    const a = 0.1 + Math.min(Math.abs(pct) / 60, 1) * 0.45;
    return { backgroundColor: pct > 0 ? `rgba(16,185,129,${a.toFixed(2)})` : `rgba(239,68,68,${a.toFixed(2)})` };
  };
  const fmtPct = (pct) => `${pct > 0 ? '+' : ''}${pct}%`;

  // Ekrandaki Total kolonu da export'larla aynı tanımda: cari yıl YTD.
  const rowTotal = (keyword) => yearTotal(monthly[keyword.toLowerCase()], currentYear, ytdMonth);

  const computeAvgMonthly = (kwKey) => {
    const arr = monthly[kwKey];
    if (!Array.isArray(arr) || !arr.length) return null;
    const sorted = [...arr].sort((a, b) => (a.year - b.year) || (a.month - b.month));
    const last12 = sorted.slice(-12);
    return Math.round(last12.reduce((s, r) => s + Number(r.monthly_searches || 0), 0) / last12.length);
  };

  const compute3moChange = (kwKey) => {
    const arr = monthly[kwKey];
    if (!Array.isArray(arr) || arr.length < 6) return null;
    const sorted = [...arr].sort((a, b) => (a.year - b.year) || (a.month - b.month));
    const sumLast3 = sorted.slice(-3).reduce((s, r) => s + Number(r.monthly_searches || 0), 0);
    const sumPrev3 = sorted.slice(-6, -3).reduce((s, r) => s + Number(r.monthly_searches || 0), 0);
    if (!sumPrev3) return null;
    return Math.round(((sumLast3 - sumPrev3) / sumPrev3) * 100);
  };

  const loadImageAsBase64 = (url) => new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      c.getContext('2d').drawImage(img, 0, 0);
      resolve(c.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

  const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('tr-TR'));
  const fmtCpc = (n) => (n == null ? '—' : `₺${Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}`);

  const competitionBadge = (c) => {
    const cls = c === 'HIGH'
      ? 'bg-red-50 text-red-700 border-red-200'
      : c === 'MEDIUM'
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : c === 'LOW'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : 'bg-gray-50 text-gray-600 border-gray-200';
    return <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded border ${cls}`}>{c || '—'}</span>;
  };

  const progressPct = totalKeywords > 0 ? Math.round((fetched / totalKeywords) * 100) : 0;

  const categories = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const g of groups) {
      if (!seen.has(g.category)) { seen.add(g.category); out.push(g.category); }
    }
    return out;
  }, [groups]);

  const handleAddKeyword = async () => {
    const kw = addKeyword.trim();
    const cat = addCategory.trim();
    if (!kw || !cat) {
      setAddError('Keyword and category are required');
      return;
    }
    setAddBusy(true);
    setAddError('');
    try {
      const brandId = localStorage.getItem('currentBrandId');
      const res = await fetch('https://api.glowytics.com/api/search-volume/keyword-groups', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) },
        body: JSON.stringify({ keyword: kw, category: cat }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        setAddError(data?.error || 'Failed to add');
        return;
      }
      const row = data.row;
      if (row) {
        setGroups(prev => {
          // remove same-id (if upserted), then append at the bottom of its category block
          const without = prev.filter(g => g.id !== row.id);
          const lastIdxOfCat = (() => {
            let idx = -1;
            without.forEach((g, i) => { if (g.category === row.category) idx = i; });
            return idx;
          })();
          if (lastIdxOfCat === -1) return [...without, row];
          return [
            ...without.slice(0, lastIdxOfCat + 1),
            row,
            ...without.slice(lastIdxOfCat + 1),
          ];
        });
        // fetch its monthly volumes
        (async () => {
          const r = await fetchMonthlyForKeyword(row.keyword);
          if (r.ok) {
            const kwk = row.keyword.toLowerCase();
            setMonthly(prev => ({ ...prev, [kwk]: r.monthly }));
            setFetchStatus(prev => ({ ...prev, [kwk]: 'ok' }));
            if (r.competition != null || r.cpcLow != null || r.cpcHigh != null) {
              setApiMetrics(prev => ({ ...prev, [kwk]: { competition: r.competition, cpcLow: r.cpcLow, cpcHigh: r.cpcHigh } }));
            }
          }
        })();
      }
      setIsAddOpen(false);
      setAddKeyword('');
      setAddCategory('');
      setNewCategoryMode(false);
    } catch (e) {
      setAddError(e?.message || 'Failed to add');
    } finally {
      setAddBusy(false);
    }
  };

  const handleDeleteRow = async (row) => {
    if (!row?.id) return;
    if (!window.confirm(`Delete "${row.keyword}"?`)) return;
    setDeletingIds(prev => ({ ...prev, [row.id]: true }));
    try {
      const brandId = localStorage.getItem('currentBrandId');
      const res = await fetch(`https://api.glowytics.com/api/search-volume/keyword-groups/${row.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { ...(brandId ? { 'X-Brand-ID': brandId } : {}) },
      });
      if (!res.ok) return;
      setGroups(prev => prev.filter(g => g.id !== row.id));
      setMonthly(prev => {
        const n = { ...prev };
        delete n[row.keyword.toLowerCase()];
        return n;
      });
    } finally {
      setDeletingIds(prev => {
        const n = { ...prev };
        delete n[row.id];
        return n;
      });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        <div className="p-6 bg-white text-gray-900 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/SearchVolume.svg" alt="Search Volume Matrix" className="w-14 h-14 object-contain" />
            <div>
              <h1 className="text-2xl tracking-tight">Search Volume Matrix</h1>
              <p className="text-sm text-gray-500">
                Categorized keyword list · Google monthly search volumes (geo: Turkey)
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Data sourced from Google Ads Keyword Planner API — based on actual search volumes, not estimates. Numbers are rounded by Google and presented as integers.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button
              onClick={() => setIsAddOpen(true)}
              className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >+ Add Keyword</button>
            {failedCount > 0 && !running && (
              <button
                onClick={runFetchFailed}
                className="px-3 py-1.5 text-xs rounded-lg border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                title="Retry keywords that failed to fetch"
              >Retry Failed ({failedCount})</button>
            )}
            <div className="relative">
              <button
                onClick={() => setExportOpen(o => !o)}
                disabled={!groups.length}
                className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >Export ▾</button>
              {exportOpen && (
                <div className="absolute right-0 mt-1 w-52 bg-white border rounded-lg shadow-lg z-30 overflow-hidden">
                  <button onClick={() => { setExportOpen(false); exportExcel(); }} className="w-full px-3 py-2 text-left text-xs hover:bg-gray-50 flex items-center gap-2">
                    <span className="text-emerald-600">⬇</span> Excel (.xlsx)
                  </button>
                  <button disabled={exportingBrand} onClick={() => { setExportOpen(false); exportExcelBrandFormat(); }} className="w-full px-3 py-2 text-left text-xs hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
                    <span className="text-emerald-600">⬇</span> {exportingBrand ? 'Hazırlanıyor…' : 'Excel (marka format · renkli)'}
                  </button>
                  <button onClick={() => { setExportOpen(false); exportPDF(); }} className="w-full px-3 py-2 text-left text-xs hover:bg-gray-50 flex items-center gap-2">
                    <span className="text-red-600">⬇</span> PDF
                  </button>
                </div>
              )}
            </div>
            {running && (
              <button
                className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => { abortRef.current = true; }}
              >Stop</button>
            )}
            <button
              disabled={running || loadingGroups || !groups.length}
              onClick={() => { setFetchStatus({}); runFetchAll(groups); }}
              className="px-3 py-1.5 text-xs rounded-lg bg-black text-white disabled:opacity-50"
            >
              {running ? `Fetching… ${fetched}/${totalKeywords}` : 'Refresh All'}
            </button>
          </div>
        </div>

        {/* Kayıtlı görüntünün tarihi */}
        {snapshotAt && !running && (
          <div className="px-6 py-2 border-b text-xs text-gray-500">
            Saved data · last updated {snapshotAt.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            {' · '}Google publishes each month mid-way through the next; refreshed automatically on the 16th.
            {' '}Use Refresh All to pull live data now.
          </div>
        )}

        {/* Isı haritası açıklaması + aç/kapa */}
        {groups.length > 0 && (
          <div className="px-6 py-2 border-b text-xs text-gray-600 flex items-center gap-3 flex-wrap">
            <button
              onClick={toggleYoyColors}
              className={`px-2.5 py-1 rounded-md border text-xs ${yoyColors ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            >{yoyColors ? 'YoY colors: on' : 'YoY colors: off'}</button>
            {yoyColors && (
              <>
                <span>Each month vs the same month last year:</span>
                <span className="inline-flex items-center gap-1">
                  {[-50, -25, -10, 10, 25, 50].map(p => (
                    <span key={p} className="inline-block w-7 h-4 rounded-sm text-[9px] leading-4 text-center text-gray-800" style={yoyStyle(p)}>{fmtPct(p)}</span>
                  ))}
                </span>
                <span className="text-gray-400">Uncoloured = within ±3% or no data last year. Hover a cell for the exact change.</span>
              </>
            )}
          </div>
        )}

        {/* Progress */}
        {(running || (totalKeywords > 0 && fetched < totalKeywords)) && (
          <div className="px-6 py-3 bg-gray-50 border-b">
            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
              <span>{running ? 'Fetching data from Google Ads Keyword Planner' : 'Waiting'}</span>
              <span>{fetched} / {totalKeywords} ({progressPct}%)</span>
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 transition-all" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        )}

        {/* 2. geçiş: son ayı eksik dönen kelimeleri yeniden çekiyoruz */}
        {repairing && (
          <div className="px-6 py-2 bg-amber-50 border-b text-xs text-amber-800">
            Son ayı eksik dönen {repairing.total} kelime yeniden çekiliyor… {repairing.done}/{repairing.total}
          </div>
        )}

        {/* Kalıcı eksikler — sessizce 0 yazmak yerine açıkça söyle */}
        {!running && missingTailKeywords.length > 0 && (
          <div className="px-6 py-3 bg-amber-50 border-b text-xs text-amber-900">
            <span className="font-semibold">{monthNames[ytdMonth - 1]} {currentYear} verisi gelmeyen {missingTailKeywords.length} kelime:</span>{' '}
            {missingTailKeywords.join(', ')}.{' '}
            Bu satırlarda ilgili ay <span className="font-mono">—</span> ile işaretli ve YTD toplamı daha kısa bir pencereyi kapsıyor;
            diğer kelimelerle birebir kıyaslamayın. Google Keyword Planner aynı isteğe zaman zaman son ayı atlayarak yanıt veriyor —
            <button onClick={runFetchShortTails} className="ml-1 underline font-medium">tekrar dene</button>.
          </div>
        )}

        {loadingGroups ? (
          <div className="px-6 py-16 text-center text-sm text-gray-500">Loading keyword list…</div>
        ) : groups.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm text-gray-500">
            No keyword groups defined for this brand.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                {/* Top row: meta cols + year-spanning headers */}
                <tr>
                  <th rowSpan={2} className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-20 border-r" style={{ minWidth: 220 }}>
                    Keyword
                  </th>
                  <th rowSpan={2} className="px-2 py-2 text-right text-[11px] font-medium text-gray-500 uppercase border-r">Avg/mo</th>
                  <th rowSpan={2} className="px-2 py-2 text-center text-[11px] font-medium text-gray-500 uppercase">3mo Δ</th>
                  <th rowSpan={2} className="px-2 py-2 text-center text-[11px] font-medium text-gray-500 uppercase">Comp</th>
                  <th rowSpan={2} className="px-2 py-2 text-right text-[11px] font-medium text-gray-500 uppercase border-r">CPC ₺</th>
                  {yearGroups.map((yg) => {
                    const isCurrent = yg.year === currentYear;
                    const isExpanded = !!expandedYears[yg.year];
                    return (
                      <th
                        key={`yg-${yg.year}`}
                        colSpan={yg.span}
                        onClick={isCurrent ? undefined : () => toggleYear(yg.year)}
                        title={isCurrent ? undefined : (isExpanded ? 'Collapse year' : 'Expand to quarters')}
                        className={`px-2 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wide border-l select-none ${
                          isCurrent ? 'bg-blue-50 text-blue-800' : 'text-gray-700 cursor-pointer hover:bg-gray-100'
                        }`}
                      >
                        {isCurrent ? yg.year : <span>{isExpanded ? '▾' : '▸'} {yg.year}</span>}
                      </th>
                    );
                  })}
                  <th rowSpan={2} className="px-2 py-2 text-right text-[11px] font-medium text-gray-500 uppercase bg-gray-100 border-l" title={`${currentYear} yılının ${ytdLabel} toplamı`}>{currentYear} YTD</th>
                </tr>
                {/* Sub row: year-total / quarter / month labels */}
                <tr>
                  {columns.map((c) => {
                    const key = `col-${c.year}-${c.type}-${c.month || c.quarter || ''}`;
                    if (c.type === 'year') {
                      return (
                        <th
                          key={key}
                          onClick={() => toggleYear(c.year)}
                          className="px-2 py-1.5 text-right text-[10px] font-medium uppercase whitespace-nowrap text-gray-500 bg-gray-100 cursor-pointer hover:bg-gray-200 select-none"
                          title="Expand to quarters"
                        >Year Total</th>
                      );
                    }
                    if (c.type === 'quarter') {
                      return (
                        <th
                          key={key}
                          onClick={() => toggleQuarter(c.year, c.quarter)}
                          className="px-2 py-1.5 text-right text-[10px] font-medium uppercase whitespace-nowrap text-gray-500 bg-gray-50 cursor-pointer hover:bg-gray-200 select-none"
                          title="Expand to months"
                        >▸ {c.label}</th>
                      );
                    }
                    return (
                      <th
                        key={key}
                        className={`px-2 py-1.5 text-right text-[10px] font-medium uppercase whitespace-nowrap text-gray-500 ${c.inQuarter ? 'bg-blue-50/40 cursor-pointer hover:text-gray-700' : ''}`}
                        onClick={c.inQuarter ? () => toggleQuarter(c.year, c.inQuarter) : undefined}
                        title={c.inQuarter ? 'Collapse quarter' : undefined}
                      >
                        {c.label}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {grouped.map((g) => (
                  <React.Fragment key={g.category}>
                    <tr className="bg-blue-50/60">
                      <td colSpan={5 + columns.length + 1} className="px-3 py-2 text-[13px] font-semibold text-blue-900 sticky left-0">
                        {g.category} <span className="text-blue-700/60 font-normal">· {g.items.length} keyword</span>
                      </td>
                    </tr>
                    {g.items.map((row) => {
                      const kwKey = row.keyword.toLowerCase();
                      const loaded = monthly[kwKey] !== undefined;
                      // Bu satırın kendi son ayı rapor penceresinden geride mi?
                      // Geride ise o aylar 0 DEĞİL "ölçüm yok" — ve YTD toplamı
                      // başlıktaki pencereyi temsil etmiyor.
                      const rowLastMonth = lastMonthOf(monthly[kwKey], currentYear);
                      const isShort = loaded && rowLastMonth > 0 && rowLastMonth < ytdMonth;
                      return (
                        <tr key={row.id} className="hover:bg-gray-50 group">
                          <td className="px-3 py-1.5 text-gray-900 sticky left-0 bg-white group-hover:bg-gray-50 border-r" style={{ minWidth: 220 }}>
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate flex items-center gap-1.5">
                                {fetchStatus[kwKey] === 'failed' && (
                                  <span title="Failed to fetch — use Retry Failed button" className="inline-block w-4 h-4 text-[10px] leading-4 text-center rounded-full bg-red-100 text-red-700 font-bold">!</span>
                                )}
                                {row.keyword}
                              </span>
                              <button
                                onClick={() => handleDeleteRow(row)}
                                disabled={!!deletingIds[row.id]}
                                title="Delete"
                                className="opacity-0 group-hover:opacity-100 transition w-5 h-5 inline-flex items-center justify-center rounded-full text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                              >
                                {deletingIds[row.id] ? '…' : '×'}
                              </button>
                            </div>
                          </td>
                          <td className="px-2 py-1.5 text-right text-gray-700 tabular-nums border-r">{fmt(row.avgMonthly ?? computeAvgMonthly(kwKey))}</td>
                          {(() => {
                            const ch = row.change3moPct ?? compute3moChange(kwKey);
                            return (
                              <td className={`px-2 py-1.5 text-center text-xs tabular-nums ${
                                ch == null ? 'text-gray-400' : ch > 0 ? 'text-emerald-700' : ch < 0 ? 'text-red-700' : 'text-gray-500'
                              }`}>
                                {ch == null ? '—' : `${ch > 0 ? '+' : ''}${ch}%`}
                              </td>
                            );
                          })()}
                          <td className="px-2 py-1.5 text-center">{competitionBadge(row.competition || apiMetrics[kwKey]?.competition)}</td>
                          {(() => {
                            const low = row.cpcLow ?? apiMetrics[kwKey]?.cpcLow;
                            const high = row.cpcHigh ?? apiMetrics[kwKey]?.cpcHigh;
                            return (
                              <td className="px-2 py-1.5 text-right text-[11px] text-gray-600 whitespace-nowrap border-r">
                                {low != null && high != null ? `${fmtCpc(low)} – ${fmtCpc(high)}` : '—'}
                              </td>
                            );
                          })()}
                          {columns.map((c) => {
                            const key = `${row.id}-${c.year}-${c.type}-${c.month || c.quarter || ''}`;
                            const cls =
                              c.type === 'year' ? 'bg-gray-100/70 font-semibold text-gray-800 border-l'
                              : c.type === 'quarter' ? 'bg-gray-50/60 font-medium text-gray-700'
                              : c.inQuarter ? 'text-gray-800 bg-blue-50/20'
                              : 'text-gray-800';
                            // Ölçüm gelmeyen ay: 0 yazma. "0" düşüş gibi okunuyor —
                            // müşteri Temmuz boşluğunu tam olarak böyle yorumladı.
                            if (isShort && c.type === 'month' && c.year === currentYear && c.month > rowLastMonth) {
                              return (
                                <td key={key} className={`px-2 py-1.5 text-right whitespace-nowrap bg-amber-50 ${cls}`}>
                                  <span className="text-amber-700" title={`Google Keyword Planner bu kelime için ${monthNames[c.month - 1]} ${currentYear} verisi döndürmedi. Bu bir düşüş değil, ölçüm eksiği.`}>—</span>
                                </td>
                              );
                            }
                            const v = loaded ? getCellValue(row.keyword, c) : null;
                            let style, title;
                            if (yoyColors && loaded && c.type === 'month') {
                              const cur = rawMonth(row.keyword, c.year, c.month);
                              const prev = rawMonth(row.keyword, c.year - 1, c.month);
                              const pct = yoyPct(cur, prev);
                              style = yoyStyle(pct);
                              title = pct == null
                                ? `${monthNames[c.month - 1]} ${c.year}: ${fmt(cur)} · no data for ${monthNames[c.month - 1]} ${c.year - 1}`
                                : `${monthNames[c.month - 1]} ${c.year}: ${fmt(cur)} · ${monthNames[c.month - 1]} ${c.year - 1}: ${fmt(prev)} · ${fmtPct(pct)} YoY`;
                            }
                            return (
                              <td key={key} style={style} title={title} className={`px-2 py-1.5 text-right tabular-nums whitespace-nowrap ${cls}`}>
                                {v == null ? <span className="text-gray-300">·</span> : fmt(v)}
                              </td>
                            );
                          })}
                          <td className={`px-2 py-1.5 text-right tabular-nums font-semibold border-l ${isShort ? 'bg-amber-50' : 'bg-gray-50'}`}>
                            {loaded ? (
                              isShort ? (
                                <span className="text-amber-800" title={`Bu satırda veri sadece ${monthNames[0]}–${monthNames[rowLastMonth - 1]} için var; toplam bu aralığı kapsıyor, başlıktaki ${ytdLabel} aralığını DEĞİL. Diğer markalarla birebir kıyaslanamaz.`}>
                                  {fmt(rowTotal(row.keyword))} <span className="text-[10px]">⚠</span>
                                </span>
                              ) : fmt(rowTotal(row.keyword))
                            ) : <span className="text-gray-300">·</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-6 py-3 bg-gray-50 border-t text-[11px] text-gray-500">
          Avg/mo, 3mo Δ, Competition and CPC columns are seeded from Google Ads Keyword Planner exports. Monthly columns come from the Keyword Planner API, saved and refreshed monthly.
        </div>
      </Card>

      {isAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => !addBusy && setIsAddOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl border w-full max-w-md mx-4">
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Add Keyword</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => !addBusy && setIsAddOpen(false)}>✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Keyword</label>
                <input
                  autoFocus
                  value={addKeyword}
                  onChange={(e) => setAddKeyword(e.target.value)}
                  placeholder="e.g. hoka mafate"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                {!newCategoryMode ? (
                  <div className="flex gap-2">
                    <select
                      value={addCategory}
                      onChange={(e) => setAddCategory(e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-lg text-sm bg-white"
                    >
                      <option value="">— Select —</option>
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <button
                      type="button"
                      onClick={() => { setNewCategoryMode(true); setAddCategory(''); }}
                      className="px-3 py-2 text-xs border rounded-lg hover:bg-gray-50"
                    >+ New</button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      value={addCategory}
                      onChange={(e) => setAddCategory(e.target.value)}
                      placeholder="New category name"
                      className="flex-1 px-3 py-2 border rounded-lg text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => { setNewCategoryMode(false); setAddCategory(''); }}
                      className="px-3 py-2 text-xs border rounded-lg hover:bg-gray-50"
                    >Cancel</button>
                  </div>
                )}
              </div>
              {addError && <div className="text-xs text-red-600">{addError}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  disabled={addBusy}
                  onClick={() => setIsAddOpen(false)}
                  className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >Cancel</button>
                <button
                  disabled={addBusy || !addKeyword.trim() || !addCategory.trim()}
                  onClick={handleAddKeyword}
                  className="px-3 py-2 text-sm rounded-lg bg-black text-white disabled:opacity-50"
                >{addBusy ? 'Adding…' : 'Add'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
